import { Suspense, useCallback, useState } from "react";
import { useVersionChecker } from "@/hooks/util/useVersionChecker";
import {
    recordVersionUpdatePrompt,
    recordVersionUpdateReload,
} from "@/lib/helpers/version-update-helper";
import { RouterProvider } from "react-router-dom";
import { router } from "@/router";
import { AuthProvider } from "@/components/contexts/AuthContext";
import { Button } from "@/components/shadcn/ui/button";
import { X } from "lucide-react";
import { LoggingProvider } from "@/components/contexts/LoggingContext";

export function VersionedApp() {
    const [isOutdated, setIsOutdated] = useState(false);

    // Check if the page is embedded (e.g., in an iframe on a public website)
    const isEmbedded = new URLSearchParams(window.location.search).get("embedded") === "true";

    const handleUpdateAvailable = useCallback(
        (version: string) => {
            if (isEmbedded) return;
            recordVersionUpdatePrompt(version);
            setIsOutdated(true);
        },
        [isEmbedded]
    );

    const handleReload = () => {
        recordVersionUpdateReload();
        window.location.reload();
    };

    useVersionChecker({
        interval: 30000,
        onUpdateAvailable: handleUpdateAvailable,
    });

    return (
        <LoggingProvider>
            <AuthProvider>
                {isOutdated && !isEmbedded && (
                    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
                        <div
                            className="flex items-center gap-3 rounded-lg px-4 py-3 shadow-lg border border-gray-600"
                            style={{ backgroundColor: "#374955" }}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-2 h-2 rounded-full animate-pulse"
                                    style={{ backgroundColor: "#06A7E0" }}
                                ></div>
                                <span className="text-sm font-medium text-white">
                                    New version available
                                </span>
                            </div>
                            <div className="flex items-center gap-2 ml-2">
                                <button
                                    onClick={handleReload}
                                    className="text-sm underline cursor-pointer hover:no-underline transition-all"
                                    style={{ color: "#06A7E0" }}
                                >
                                    Refresh
                                </button>
                                <Button
                                    onClick={() => setIsOutdated(false)}
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-gray-300 hover:text-white hover:bg-gray-600"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                <Suspense
                    fallback={
                        <div className="flex items-center justify-center h-screen">
                            <div className="w-8 h-8 border-4 border-gray-300 border-t-green-600 rounded-full animate-spin" />
                        </div>
                    }
                >
                    <RouterProvider router={router} />
                </Suspense>
            </AuthProvider>
        </LoggingProvider>
    );
}
